<template>
    <v-row class="calendar-filters" dense>
        <v-col cols="12" md="3">
            <v-select
                v-model="model.serviceId"
                :items="groupedRoomTypes"
                :label="$t(isOwnHotel ? 'supplier.room_type' : 'supplier.service_type')"
                item-title="title"
                item-value="value"
            >
                <template v-slot:item="{props, item}">
                    <v-list-item
                        :value="item.value"
                        :class="{'pl-8': item.raw.isIndented}"
                        :style="item.raw.isIndented ? 'border-left: 2px solid #e0e0e0; margin-left: 16px;' : ''"
                        @click="props.onClick"
                    >
                        <template v-if="item.raw.isIndented" v-slot:prepend>
                            <span class="text-grey-darken-1 mr-2">└─</span>
                        </template>
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                    </v-list-item>
                </template>
            </v-select>
        </v-col>
        <v-col cols="12" md="4">
            <date-range-m-extended
                :popover="{placement: 'bottom'}"
                :show-icon="false"
                :single-line="false"
                :disable-min-start-date="true"
                style="margin-top: -20px; display: block"
                :_start="model.dateFrom"
                :_end="model.dateTo"
                @change-start="model.dateFrom = $event"
                @change-end="model.dateTo = $event"
            />
        </v-col>
        <v-col cols="12" md="3">
            <v-select
                v-model="model.dateRangeId"
                :items="dateRange"
                :label="$t('supplier.date_range')"
                item-title="name"
                item-value="id"
                @update:model-value="updateDates"
            />
        </v-col>
        <v-col cols="12" md="2">
            <v-btn color="primary" @click="update">
                {{ $t('supplier.update') }}
            </v-btn>
        </v-col>
    </v-row>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Vue, Watch} from 'vue-facing-decorator'
    import DateRangeMExtended from '~src/components/supplier/DateRangeMExtended.vue'
    import {supplierStore} from '~src/composables/store-accessor'

    @Component({
        components: {DateRangeMExtended},
    })
    export class CalendarFilters extends Vue {
        @Prop() startDate
        @Prop() endDate
        @Prop() services
        @Prop({required: true}) contract

        model = {
            dateFrom: null,
            dateTo: null,
            serviceId: 0,
            dateRangeId: 0,
        }

        roomTypes = []
        hotelServices = []
        roomTypesLoaded = false
        hotelServicesLoaded = false

        async mounted() {
            this.model.dateFrom = this.startDate
            this.model.dateTo = this.endDate

            // Load room type and hotel service data for grouping
            await Promise.all([this.loadRoomTypes(), this.loadHotelServices()])
        }

        async loadRoomTypes() {
            if (this.roomTypesLoaded || !this.isOwnHotel) return

            try {
                // Get hotel ID from supplier store
                const hotelId = supplierStore.hotelInfo?.hotelId
                if (!hotelId) {
                    console.log('No hotel ID found in supplier store')
                    this.roomTypesLoaded = true
                    return
                }

                const response = await this.$api.hotelDescriptionRoomType.get(hotelId, {locale: 'all'})
                this.roomTypes = response.hotelRoomTypes || []
                this.roomTypesLoaded = true
            } catch (error) {
                console.error('Error loading room types:', error)
                this.roomTypes = []
                this.roomTypesLoaded = true
            }
        }

        async loadHotelServices() {
            if (this.hotelServicesLoaded || !this.isOwnHotel) return

            try {
                const response = await this.$api.ownHotelServices.get({contractId: this.contract?.id})
                this.hotelServices = response.services || []
                this.hotelServicesLoaded = true
            } catch (error) {
                console.error('Error loading hotel services:', error)
                this.hotelServices = []
                this.hotelServicesLoaded = true
            }
        }

        getRoomTypeByServiceId(serviceId) {
            // First try to find the service in hotel services to get the room type ID
            const hotelService = this.hotelServices.find(service => service.id === serviceId)
            if (hotelService && hotelService.hotelRoomTypeId) {
                // Use the hotelRoomTypeId from hotel service to find the room type
                const roomType = this.roomTypes.find(rt => rt.id === hotelService.hotelRoomTypeId)
                if (roomType) {
                    return roomType
                }
            }

            // Fallback: try direct serviceId match with room type ID
            const directMatch = this.roomTypes.find(rt => rt.id === serviceId)
            if (directMatch) {
                return directMatch
            }

            return null
        }

        get groupedRoomTypes() {
            if (!this.services || this.services.length === 0) {
                return [{value: 0, title: this.$t('supplier.all'), isIndented: false}]
            }

            // If not a hotel or data not loaded yet, return basic format
            if (!this.isOwnHotel || !this.roomTypesLoaded || !this.hotelServicesLoaded) {
                const basicServices = this.services.map(service => ({
                    value: service.id,
                    title: service.name,
                    isIndented: false,
                }))

                basicServices.unshift({value: 0, title: this.$t('supplier.all'), isIndented: false})
                return basicServices
            }

            // Use a Map to group services by their basic room ID
            const basicRoomGroups = new Map()

            // Check if we have any room types at all
            if (this.roomTypes.length === 0) {
                const basicServices = this.services.map(service => ({
                    value: service.id,
                    title: `${service.name} (BASIC)`,
                    originalTitle: service.name,
                    sustainabilityLevel: 'BASIC',
                    isIndented: false,
                }))

                basicServices.unshift({value: 0, title: this.$t('supplier.all'), isIndented: false})
                return basicServices
            }

            // First pass: organize services by their basic room ID
            this.services.forEach(service => {
                const roomType = this.getRoomTypeByServiceId(service.id)

                if (!roomType) {
                    // If no room type found, treat as standalone service with BASIC label
                    const standaloneId = `standalone_${service.id}`
                    basicRoomGroups.set(standaloneId, {
                        basicRoomId: standaloneId,
                        basicRoomName: service.name,
                        services: {
                            BASIC: service,
                            SUSTAINABLE: [],
                            ZERO: [],
                        },
                    })
                    return
                }

                const sustainabilityLevel = roomType.sustainabilityLevel || 'BASIC'
                let basicRoomId

                if (sustainabilityLevel === 'BASIC') {
                    basicRoomId = roomType.id
                } else {
                    // For SUSTAINABLE/ZERO rooms, find their basic room ID
                    basicRoomId = roomType.basicRoomId
                    if (!basicRoomId) {
                        // If no basicRoomId, treat as standalone
                        const standaloneId = `standalone_${service.id}`
                        basicRoomGroups.set(standaloneId, {
                            basicRoomId: standaloneId,
                            basicRoomName: service.name,
                            services: {
                                BASIC: null,
                                SUSTAINABLE: sustainabilityLevel === 'SUSTAINABLE' ? [service] : [],
                                ZERO: sustainabilityLevel === 'ZERO' ? [service] : [],
                            },
                        })
                        return
                    }
                }

                // Create or get the group for this basic room ID
                if (!basicRoomGroups.has(basicRoomId)) {
                    basicRoomGroups.set(basicRoomId, {
                        basicRoomId,
                        basicRoomName: '',
                        services: {
                            BASIC: null,
                            SUSTAINABLE: [],
                            ZERO: [],
                        },
                    })
                }

                const group = basicRoomGroups.get(basicRoomId)

                // Add service to appropriate sustainability level
                if (sustainabilityLevel === 'BASIC') {
                    group.services.BASIC = service
                    group.basicRoomName = service.name
                } else if (sustainabilityLevel === 'SUSTAINABLE') {
                    group.services.SUSTAINABLE.push(service)
                } else if (sustainabilityLevel === 'ZERO') {
                    group.services.ZERO.push(service)
                }
            })

            // Second pass: convert groups to flat list with proper ordering and indentation
            const groupedServices = []

            Array.from(basicRoomGroups.values())
                .sort((a, b) => a.basicRoomName.localeCompare(b.basicRoomName))
                .forEach(group => {
                    // Add BASIC room first
                    if (group.services.BASIC) {
                        groupedServices.push({
                            value: group.services.BASIC.id,
                            title: `${group.services.BASIC.name} (BASIC)`,
                            originalTitle: group.services.BASIC.name,
                            sustainabilityLevel: 'BASIC',
                            isIndented: false,
                        })
                    }

                    // Add SUSTAINABLE rooms (indented)
                    group.services.SUSTAINABLE.forEach(service => {
                        groupedServices.push({
                            value: service.id,
                            title: `${service.name} (SUSTAINABLE)`,
                            originalTitle: service.name,
                            sustainabilityLevel: 'SUSTAINABLE',
                            isIndented: true,
                        })
                    })

                    // Add ZERO rooms (indented)
                    group.services.ZERO.forEach(service => {
                        groupedServices.push({
                            value: service.id,
                            title: `${service.name} (ZERO)`,
                            originalTitle: service.name,
                            sustainabilityLevel: 'ZERO',
                            isIndented: true,
                        })
                    })
                })

            // Add "All" option at the beginning
            groupedServices.unshift({value: 0, title: this.$t('supplier.all'), isIndented: false})

            return groupedServices
        }

        get isOwnHotel() {
            return this.contract?.serviceType === 'ownHotel'
        }

        get dateRange() {
            return [
                {id: 0, name: this.$t('supplier.choose_period')},
                {id: 1, name: this.$t('supplier.last_month')},
                {id: 2, name: this.$t('supplier.next_month')},
                {id: 3, name: this.$t('supplier.this_time_last_year')},
            ]
        }

        @Watch('model', {deep: true})
        onModelChange() {
            this.update()
        }

        @Emit()
        update() {
            return this.model
        }

        updateDates(dateRangeId) {
            const today = new Date()
            const currentMonth = today.getMonth()
            const currentYear = today.getFullYear()

            switch (dateRangeId) {
                case 1: // Last month
                    this.model.dateFrom = new Date(currentYear, currentMonth - 1, 1)
                    this.model.dateTo = new Date(currentYear, currentMonth, 0)
                    break
                case 2: // Next month
                    this.model.dateFrom = new Date(currentYear, currentMonth + 1, 1)
                    this.model.dateTo = new Date(currentYear, currentMonth + 2, 0)
                    break
                case 3: // This time last year
                    this.model.dateFrom = new Date(currentYear - 1, currentMonth, today.getDate())
                    this.model.dateTo = new Date(currentYear - 1, currentMonth + 1, 0)
                    break
                default:
                    // Choose period - don't change dates
                    break
            }
        }
    }

    export default CalendarFilters
</script>

<style scoped lang="scss">
    .calendar-filters {
        background: white;
        border-radius: 8px;
        padding: 16px;
        margin-bottom: 16px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    // ZSH theme styling for room type select
    // Target .v-field__outline (fieldset) instead of .v-field to preserve
    // the legend notch that creates a gap for the floating label.
    // Adding a border on .v-field directly causes a "strike-through" line
    // through the label text because .v-field has no notch mechanism.
    :deep(.v-select) {
        .v-field__outline {
            border-color: var(--zsh-primary, #3699a4);
        }

        &:hover .v-field__outline {
            border-color: var(--zsh-primary-dark, #2a7a84);
        }
    }
</style>
